// packages/core/echo/text-model/src/text-model.ts
import assert from "@dxos/node-std/assert";
import { Doc, Text, XmlElement, XmlText, XmlFragment, applyUpdate, encodeStateAsUpdate } from "yjs";
import { Model } from "@dxos/model-factory";
import { schema } from "@dxos/protocols";
import { TextKind } from "@dxos/protocols/proto/dxos/echo/model/text";
var TextModelStateMachine = class {
  constructor() {
    this._text = {
      doc: new Doc(),
      kind: TextKind.PLAIN,
      field: "content"
    };
  }
  getState() {
    return this._text;
  }
  process(mutation) {
    const { update, clientId, kind, field } = mutation;
    if (kind) {
      this._text.kind = kind;
    }
    if (field) {
      this._text.field = field;
    }
    if (update && clientId !== this._text.doc.clientID) {
      assert(update.length > 0, "update buffer is empty");
      applyUpdate(this._text.doc, update, {
        docClientId: clientId
      });
    }
  }
  snapshot() {
    return {
      data: encodeStateAsUpdate(this._text.doc),
      kind: this._text.kind,
      field: this._text.field
    };
  }
  reset(snapshot) {
    assert(snapshot.data);
    applyUpdate(this._text.doc, snapshot.data);
    this._text.kind = snapshot.kind;
    this._text.field = snapshot.field;
  }
};
var TextModel = class extends Model {
  // prettier-ignore
  constructor(meta, itemId, getState, writeStream) {
    super(meta, itemId, getState, writeStream);
    this._textContentInner = (node) => {
      if (node instanceof Text) {
        return node.toString();
      }
      if (node.length === 0) {
        return node instanceof XmlFragment ? "" : "\n";
      }
      const textContentNodes = [];
      const nodes = node.toArray();
      for (const childNode of nodes) {
        textContentNodes.push(this._textContentInner(childNode));
      }
      return textContentNodes.join("\n");
    };
    this._insertInner = (node, index, text) => {
      if (node instanceof XmlText) {
        if (index <= node.length) {
          node.insert(index, text);
          return true;
        }
        return node.length;
      }
      let innerIndex = index;
      let childLength = 0;
      if (node instanceof XmlFragment && node.length === 0) {
        const paragraph = new XmlElement("paragraph");
        paragraph.insert(0, [
          new XmlText("")
        ]);
        node.insert(0, [
          paragraph
        ]);
      }
      for (const childNode of node.toArray()) {
        const inserted = this._insertInner(childNode, innerIndex, text);
        if (inserted === true) {
          return true;
        }
        childLength += inserted;
        innerIndex -= inserted + 1;
      }
      return childLength;
    };
    this.initialize();
  }
  initialize() {
    var _a;
    (_a = this._unsubscribe) == null ? void 0 : _a.call(this);
    this._unsubscribe = this._subscribeToDocUpdates();
  }
  get doc() {
    return this._getState().doc;
  }
  get kind() {
    return this._getState().kind;
  }
  get field() {
    return this._getState().field;
  }
  get content() {
    switch (this.kind) {
      case TextKind.RICH:
        return this.doc.getXmlFragment(this.field);
      default:
        return this.doc.getText(this.field);
    }
  }
  // TODO(burdon): How is this different?
  get textContent() {
    return this._textContentInner(this.content);
  }
  _subscribeToDocUpdates() {
    const cb = this._handleDocUpdated.bind(this);
    const doc = this.doc;
    doc.on("update", cb);
    return () => {
      doc.off("update", cb);
    };
  }
  async _handleDocUpdated(update, origin) {
    const remote = origin && origin.docClientId && origin.docClientId !== this.doc.clientID;
    if (!remote) {
      await this.write({
        clientId: this.doc.clientID,
        update
      });
    }
  }
  _transact(fn) {
    return this.doc.transact(fn, {
      docClientId: this.doc.clientID
    });
  }
  insert(text, index) {
    const content = this.content;
    if (content instanceof Text) {
      return this._transact(() => content.insert(index, text));
    } else {
      return this._transact(() => this._insertInner(content, index, text));
    }
  }
  /**
  * Creates a new <paragrapgh> element with the given text content and inserts it at the given index.
  *
  * Throws if the `Text` instance is plain text.
  */
  insertTextNode(text, index = 0) {
    assert(this.kind === TextKind.RICH, "insertTextNode only supported for rich text");
    const paragraph = new XmlElement("paragraph");
    const yXmlText = new XmlText(text);
    paragraph.insert(0, [
      yXmlText
    ]);
    const content = this.content;
    if (content instanceof XmlFragment) {
      return this._transact(() => content.insert(index, [
        paragraph
      ]));
    }
  }
};
TextModel.meta = {
  type: "dxos:model/text",
  stateMachine: () => new TextModelStateMachine(),
  mutationCodec: schema.getCodecForType("dxos.echo.model.text.TextMutation"),
  snapshotCodec: schema.getCodecForType("dxos.echo.model.text.TextSnapshot")
};

// packages/core/echo/text-model/src/index.ts
import { Doc as Doc2, Text as Text2, XmlFragment as XmlFragment2 } from "yjs";
export {
  Doc2 as Doc,
  TextModel,
  Text2 as YText,
  XmlFragment2 as YXmlFragment
};
//# sourceMappingURL=index.mjs.map
